import { API_BASE_URL } from "../config/api";
import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getContactNumberWarning, getGstinWarning } from "../utils/formValidation";
import { fetchLocationByPincode, normalizePincode } from "../utils/locationApi";

const inputClassName =
  "w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm text-stone-700 outline-none transition focus:border-amber-400";
const warningClassName = "mt-1 text-xs text-red-500";

const emptyForm = {
  supplierCode: "",
  name: "",
  companyName: "",
  phone: "",
  email: "",
  gstNumber: "",
  address: {
    street: "",
    area: "",
    city: "",
    state: "",
    pincode: ""
  },
  notes: ""
};

export default function CreateDealer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const dealerBasePath = location.pathname.startsWith("/admin") ? "/admin/dealers" : "/accounts/dealers";
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [locationStatus, setLocationStatus] = useState({ loading: false, message: "", tone: "idle" });
  const phoneWarning = getContactNumberWarning(form.phone);
  const gstWarning = getGstinWarning(form.gstNumber);

  useEffect(() => {
    if (!isEditMode) return;

    const loadSupplier = async () => {
      try {
        const res = await axios.get(API_BASE_URL + `/api/dealers/${id}`);
        const dealer = res.data.dealer || {};
        setForm({
          ...emptyForm,
          ...dealer,
          supplierCode: dealer.supplierCode || "",
          name: dealer.name || "",
          companyName: dealer.companyName || "",
          phone: dealer.phone || "",
          email: dealer.email || "",
          gstNumber: dealer.gstNumber || "",
          address: {
            ...emptyForm.address,
            ...(dealer.address || {})
          },
          notes: dealer.notes || ""
        });
      } catch (error) {
        alert(error.response?.data?.message || "Failed to load supplier");
        navigate(dealerBasePath);
      } finally {
        setInitialLoading(false);
      }
    };

    loadSupplier();
  }, [dealerBasePath, id, isEditMode, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleAddressChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === "pincode" ? normalizePincode(value) : value;

    setForm((current) => ({
      ...current,
      address: {
        ...current.address,
        [name]: nextValue
      }
    }));

    if (name === "pincode" && nextValue.length !== 6) {
      setLocationStatus({ loading: false, message: "", tone: "idle" });
    }
  };

  const handlePincodeBlur = async () => {
    const pincode = normalizePincode(form.address.pincode);

    if (!pincode) {
      setLocationStatus({ loading: false, message: "", tone: "idle" });
      return;
    }

    if (pincode.length !== 6) {
      setLocationStatus({ loading: false, message: "Enter a valid 6-digit pincode.", tone: "error" });
      return;
    }

    setLocationStatus({ loading: true, message: "Looking up city and state...", tone: "loading" });

    try {
      const location = await fetchLocationByPincode(pincode);
      setForm((current) => ({
        ...current,
        address: {
          ...current.address,
          city: location.city || current.address.city,
          state: location.state || current.address.state
        }
      }));
      setLocationStatus({
        loading: false,
        message: "City and state updated from pincode.",
        tone: "success"
      });
    } catch (error) {
      setLocationStatus({
        loading: false,
        message: error.response?.data?.message || error.message || "Unable to fetch location.",
        tone: "error"
      });
    }
  };

  const locationMessageClassName =
    locationStatus.tone === "error"
      ? warningClassName
      : locationStatus.tone === "success"
        ? "mt-1 text-xs text-emerald-600"
        : "mt-1 text-xs text-stone-500";

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const res = isEditMode
        ? await axios.put(API_BASE_URL + `/api/dealers/${id}`, form)
        : await axios.post(API_BASE_URL + "/api/dealers", form);
      navigate(`${dealerBasePath}/${res.data._id || id}`);
    } catch (error) {
      alert(error.response?.data?.message || `Failed to ${isEditMode ? "update" : "create"} supplier`);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <div className="p-6 text-stone-500">Loading supplier...</div>;
  }

  return (
    <div className="min-h-screen bg-amber-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <section className="rounded-[28px] border border-amber-200 bg-[linear-gradient(135deg,_rgba(255,251,235,1),_rgba(255,255,255,1),_rgba(254,243,199,0.9))] p-6 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-amber-700">
            Supplier Register
          </p>
          <h1 className="mt-2 text-3xl font-bold text-stone-950">{isEditMode ? "Edit Supplier" : "Create Supplier"}</h1>
        </section>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <section className="rounded-[28px] border border-amber-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-stone-900">Supplier Details</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-700">Supplier Code</label>
                <input name="supplierCode" value={form.supplierCode} onChange={handleChange} className={inputClassName} placeholder="Your supplier code" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-700">Company Name</label>
                <input name="companyName" value={form.companyName} onChange={handleChange} className={inputClassName} required />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-700">Contact Name</label>
                <input name="name" value={form.name} onChange={handleChange} className={inputClassName} required />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-700">Phone / Telephone</label>
                <input name="phone" value={form.phone} onChange={handleChange} className={inputClassName} />
                {phoneWarning ? <p className={warningClassName}>{phoneWarning}</p> : null}
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-700">Email</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} className={inputClassName} />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-stone-700">GST Number</label>
                <input name="gstNumber" value={form.gstNumber} onChange={handleChange} className={inputClassName} />
                {gstWarning ? <p className={warningClassName}>{gstWarning}</p> : null}
              </div>
            </div>
          </section>

          <section className="rounded-[28px] border border-amber-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-stone-900">Address</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-stone-700">Street</label>
                <input name="street" value={form.address.street} onChange={handleAddressChange} className={inputClassName} />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-700">Pincode</label>
                <input
                  name="pincode"
                  value={form.address.pincode}
                  onChange={handleAddressChange}
                  onBlur={handlePincodeBlur}
                  className={inputClassName}
                />
                {locationStatus.message ? (
                  <p className={locationMessageClassName}>{locationStatus.message}</p>
                ) : null}
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-700">City</label>
                <input name="city" value={form.address.city} onChange={handleAddressChange} className={inputClassName} />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-700">State</label>
                <input name="state" value={form.address.state} onChange={handleAddressChange} className={inputClassName} />
              </div>
            </div>
          </section>

          <section className="rounded-[28px] border border-amber-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-stone-900">Notes</h2>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              className={`${inputClassName} mt-5 min-h-32 resize-y`}
              placeholder="Materials handled, payment notes, or contact instructions"
            />
          </section>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(dealerBasePath)}
              className="rounded-2xl bg-stone-200 px-6 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`rounded-2xl px-6 py-3 text-sm font-semibold text-stone-950 transition ${
                loading ? "bg-amber-200" : "bg-amber-500 hover:bg-amber-400"
              }`}
            >
              {loading ? "Saving..." : isEditMode ? "Update Supplier" : "Create Supplier"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


